/**
 * `/transparency` — the public "exactly what we collect" disclosure (RO-3-12 /
 * #44305). Port of `app/transparency/page.tsx` (33 LOC).
 *
 * ── This route discharges a debt RO-3-00 assigned to this item ──
 *
 * `client/shared/onboarding/telemetry-disclosure.tsx` was ported by RO-3-00
 * (#44398) with its `disclosure` deliberately left as a PROP, and its header names
 * the owner of the missing half: "There is no mounted Operation that returns a
 * `TelemetryDisclosure`, so whoever ports `/transparency` and Settings ▸ Usage
 * analytics (RO-3-12 / RO-3-09) owes one." So this item added
 * `telemetry.disclosure` to the contract and mounted it on the Hono host
 * (`server/routes/tail.ts`, beside the three telemetry ops already there).
 *
 * The panel itself is IMPORTED, never re-ported — rule A of the shared seam's
 * contract. A second copy of it in this file is the exact failure RO-3-00 exists
 * to prevent, and it would have been an easy one to make: the original page
 * imported it from `@/onboarding/telemetry-disclosure`, which still exists.
 *
 * ── The server round-trip is the whole change, and it is the RIGHT change ──
 *
 * The Next page called `describeTelemetry()` inline in a server component. That
 * function is pure (its own header: "no DB, no network, no `server-only`"), so it
 * was tempting to reach for the other option — expose it on a client-safe
 * `@excellent/core/<leaf>` subpath and keep the render synchronous. Rejected on
 * purpose:
 *
 *   1. `packages/core/package.json`'s export map is RO-4-04's file (#44295), and
 *      adding a subpath from here forks a narrowing effort that is mid-flight.
 *   2. It would ship the disclosure spec into the CLIENT BUNDLE, where it is dead
 *      weight for every route that is not this one.
 *   3. Settings ▸ Usage analytics (RO-3-09) needs the same data and will want an
 *      endpoint regardless, which is precisely why RO-3-00 specified one.
 *
 * ── Public, and it has to stay that way ──
 *
 * This page rendered unauthenticated on the Next stack — the pre-onboarding
 * consent screen links to it, and it is the public artifact the whole
 * open-analytics trust claim rests on. Moving its data onto an API call is exactly
 * where that property could have been dropped silently: on any install with a
 * credential registered, a non-public `/api/*` route requires a session, so the
 * page would have rendered its header and then failed the fetch with a 401.
 *
 * It does not, because the op's path is `/api/telemetry/disclosure` and
 * `/api/telemetry/` is ALREADY the 9th entry in `PUBLIC_PREFIXES`
 * (`packages/core/src/auth/route-gate.ts`). No prefix was added, no perimeter
 * widened, and emphatically no `publicMutation` — that state describes a
 * deliberate WRITE on a POST, and `classifyMount` throws at boot if it is attached
 * to a read. The mount carries the `READ` sentinel, which is honest rather than
 * merely method-shaped: `describeTelemetry` reads two static tables from a
 * zero-import module, so following its calls out transitively reaches no writer
 * and seeds nothing on first call.
 *
 * ── Known gap, deliberately not papered over ──
 *
 * The source exported Next `metadata` (`title: "What we collect — Excellent"`).
 * This client shell has NO document-title mechanism yet — no ported route sets
 * one, and `index.html` hard-codes `<title>Excellent</title>`. Inventing one here
 * would be a cross-cutting shell concern smuggled into a section item, so the tab
 * title is the gap this route knowingly carries; it is filed with the other
 * findings in the task rather than hidden. Same for `/skeletons` in `misc.tsx`.
 */

import { createRoute } from "@tanstack/react-router";
import { useApi } from "@excellent/ui/api";
import { telemetry } from "@excellent/ui/api/contracts/telemetry";
import { TelemetryDisclosurePanel } from "../shared";
import { rootRoute } from "./__root";

function Transparency() {
  const { data, error, isLoading } = useApi(telemetry.disclosure, {});

  return (
    <main className="mx-auto max-w-2xl px-6 py-12">
      {/* Header copy is verbatim from the source — this is the page's trust
          claim, and paraphrasing it would be a product change wearing a port's
          clothes. */}
      <header className="mb-6">
        <p className="type-subtext font-medium uppercase tracking-[0.14em] text-primary">Transparency</p>
        <h1 className="mt-2 type-page-title font-medium tracking-tight text-foreground">What we collect</h1>
        <p className="mt-2 type-body text-muted-foreground">
          Excellent is local-first — your CRM, customers, money, files and anything you type stay on your machine.
          The only thing that can ever leave is <strong>anonymous, opt-in usage telemetry</strong>: which features
          get used and where setup stalls. It is structurally impossible for content or personal data to be in it,
          and this page is generated live from the exact code that decides what may be sent. Appearing in the{" "}
          <strong>public, open metrics</strong> is a separate, off-by-default choice you make in Settings.
        </p>
      </header>

      {/* The Next page had no loading or error state to port — it rendered
          synchronously on the server. These two lines are the minimum the
          transport change forces, in the same idiom as the other ported routes. */}
      {isLoading && !data && <p className="type-body text-muted-foreground">Loading…</p>}
      {error && !data && <p className="type-body text-destructive">{String(error.message ?? error)}</p>}
      {data && <TelemetryDisclosurePanel disclosure={data} />}
    </main>
  );
}

export const transparencyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/transparency",
  component: Transparency,
});
